import {
  AssignCarrierDrawer,
  BookingDetailDrawer,
  ConfirmActionDialog,
  CustomerContactFormDrawer,
  UserFormDrawer,
  resolveBookingDetailCarrierHandlers,
} from "design-components";
import { Box, Typography } from "@mui/material";
import type { ApiCustomerDetail, ApiCustomerLocationCapacityRow } from "api-client";
import { CreateBookingWizard } from "../../bookings/components/create-booking-wizard.component";
import { EditBookingWizard } from "../../bookings/components/edit-booking-wizard.component";
import { BookingDeleteDrawer } from "../../bookings/components/booking-delete-drawer.component";
import {
  AssociateLocationDrawer,
} from "./associate-location-drawer.component";
import type { useCustomerDetailBookingsActions } from "../hooks/use-customer-detail-bookings-actions";
import type { useCustomerDetailContactsActions } from "../hooks/use-customer-detail-contacts-actions";
import type { useCustomerDetailLocationsActions } from "../hooks/use-customer-detail-locations-actions";
import type { useCustomerDetailOperatorActions } from "../hooks/use-customer-detail-operator-actions";
import { resolveCustomerOperatorId } from "../utils/customer-detail.page.utils";
import type { AppDispatch } from "../../../shared/store/store";
import { fetchBookings } from "../../../shared/store/bookings";
import type { useNotificationSnackbar } from "design-components";

type BookingsActions = ReturnType<typeof useCustomerDetailBookingsActions>;
type ContactsActions = ReturnType<typeof useCustomerDetailContactsActions>;
type LocationsActions = ReturnType<typeof useCustomerDetailLocationsActions>;
type OperatorActions = ReturnType<typeof useCustomerDetailOperatorActions>;
type ShowNotification = ReturnType<typeof useNotificationSnackbar>["showNotification"];

export interface CustomerDetailOverlaysProps {
  customer: ApiCustomerDetail;
  customerLocations: ApiCustomerLocationCapacityRow[];
  customerRoleOptions: string[];
  contactsRoleOptions: Array<{ code: string; label: string }>;
  contactEditLoading: boolean;
  contactFormSubmitting: boolean;
  userInviteSubmitting: boolean;
  statusSubmitting: boolean;
  lockedCreateBookingCustomer: { value: string; label: string } | null;
  bookings: BookingsActions;
  contacts: ContactsActions;
  locations: LocationsActions;
  operator: OperatorActions;
  dispatch: AppDispatch;
  showNotification: ShowNotification;
}

export const CustomerDetailOverlays = ({
  customer,
  customerLocations,
  customerRoleOptions,
  contactsRoleOptions,
  contactEditLoading,
  contactFormSubmitting,
  userInviteSubmitting,
  statusSubmitting,
  lockedCreateBookingCustomer,
  bookings,
  contacts,
  locations,
  operator,
  dispatch,
  showNotification,
}: CustomerDetailOverlaysProps) => {
  const operatorId = resolveCustomerOperatorId(customer);

  return (
    <>
      <UserFormDrawer
        mode="invite"
        open={operator.inviteUserDrawerOpen}
        onClose={operator.handleInviteUserDrawerClose}
        roleOptions={customerRoleOptions}
        roleSelectionMode="single"
        onSubmit={operator.handleInviteUserSubmit}
        submitting={userInviteSubmitting}
      />
      <CustomerContactFormDrawer
        mode={contacts.contactDrawerMode}
        open={contacts.contactDrawerOpen}
        onClose={contacts.handleContactDrawerClose}
        initialValues={contacts.contactFormInitialValues}
        roleOptions={contactsRoleOptions}
        onSubmit={contacts.handleContactFormSubmit}
        loading={contacts.contactDrawerMode === "edit" && contactEditLoading}
        submitting={contactFormSubmitting}
      />
      <AssociateLocationDrawer
        open={locations.associateLocationDrawerOpen}
        onClose={locations.handleAssociateLocationDrawerClose}
        mode={locations.locationDrawerMode}
        customerId={String(customer.customerId)}
        excludeLocationIds={customerLocations
          .filter((l) => l.locationId !== locations.editLocationRow?.locationId)
          .map((l) => l.locationId)}
        editRow={locations.editLocationRow}
        onSubmitCreate={locations.handleAssociateLocationCreate}
        onSubmitEdit={locations.handleAssociateLocationEdit}
        submitting={locations.locationSubmitting}
        submitError={locations.locationSubmitError}
      />
      <ConfirmActionDialog
        open={contacts.deleteContactsOpen}
        onClose={contacts.handleDeleteContactsClose}
        title="Delete contact"
        description={contacts.deleteContactsDescription}
        primaryLabel="Delete"
        onPrimaryClick={() => {
          void contacts.handleDeleteContactsConfirm();
        }}
        primaryDisabled={contacts.deleteContactsTargetIds.length === 0}
        onSecondaryClick={contacts.handleDeleteContactsClose}
      />
      <ConfirmActionDialog
        open={locations.deleteLocationOpen}
        onClose={locations.handleDeleteLocationClose}
        title="Remove location"
        description={locations.deleteLocationDescription}
        primaryLabel="Remove"
        onPrimaryClick={() => {
          void locations.handleDeleteLocationConfirm();
        }}
        primaryDisabled={!locations.deleteLocationTarget}
        primaryLoading={locations.deleteLocationSubmitting}
        onSecondaryClick={locations.handleDeleteLocationClose}
        errorText={locations.deleteLocationError}
      />
      <ConfirmActionDialog
        open={operator.statusConfirmOpen}
        onClose={operator.handleStatusConfirmClose}
        title={operator.statusConfirmTitle}
        description={operator.statusConfirmDescription}
        primaryLabel={operator.statusConfirmPrimaryLabel}
        onPrimaryClick={() => {
          void operator.handleStatusConfirm();
        }}
        primaryLoading={statusSubmitting}
        onSecondaryClick={operator.handleStatusConfirmClose}
        errorText={operator.statusConfirmError}
      />
      <ConfirmActionDialog
        open={operator.deleteConfirmOpen}
        onClose={operator.handleDeleteConfirmClose}
        title={operator.deleteConfirmCopy.title}
        description={
          <>
            <Typography
              component="span"
              sx={{
                display: "block",
                mb: operator.deleteConfirmCopy.descriptionParts.deactivateNote ? 1.5 : 0,
              }}
            >
              {operator.deleteConfirmCopy.descriptionParts.lead}
              <Box component="strong" sx={{ fontWeight: 150 }}>
                {operator.deleteConfirmCopy.descriptionParts.boldCount}
              </Box>
              {operator.deleteConfirmCopy.descriptionParts.mid}
            </Typography>
            {operator.deleteConfirmCopy.descriptionParts.deactivateNote ? (
              <Typography component="span" sx={{ display: "block" }}>
                {operator.deleteConfirmCopy.descriptionParts.deactivateNote}
              </Typography>
            ) : null}
          </>
        }
        primaryLabel={operator.deleteConfirmCopy.primaryLabel}
        primaryActionVariant={operator.deleteConfirmCopy.primaryActionVariant}
        onPrimaryClick={() => {
          void operator.handleDeleteConfirm();
        }}
        primaryLoading={operator.deleteConfirmSubmitting}
        primaryDisabled={operator.deleteConfirmPrimaryDisabled}
        onSecondaryClick={operator.handleDeleteConfirmClose}
        errorText={operator.deleteConfirmError}
        maxWidthPx={560}
      />
      <CreateBookingWizard
        open={operator.createBookingOpen}
        onClose={operator.handleCloseCreateBooking}
        operatorId={operatorId}
        lockedCustomer={lockedCreateBookingCustomer}
        onBookingCreated={(data) => {
          showNotification({
            variant: "success",
            title: "Booking created",
            description: data.bookingNumber
              ? `Booking ${data.bookingNumber} created successfully.`
              : "Booking created successfully.",
          });
          dispatch(fetchBookings());
        }}
      />
      <EditBookingWizard
        open={bookings.editBookingOpen}
        bookingId={bookings.editBookingId}
        onClose={bookings.handleCloseEditBooking}
        operatorId={operatorId}
        onBookingUpdated={bookings.handleEditBookingUpdated}
      />
      <AssignCarrierDrawer
        open={bookings.assignCarrierBooking != null}
        onClose={bookings.handleCloseAssignCarrier}
        booking={bookings.assignCarrierBooking}
        carrierOptions={bookings.carrierAssignSelectOptions}
        carriersLoading={bookings.assignDrawerCarriersLoading}
        onAssign={bookings.handleConfirmAssignCarrier}
        onDotLookup={bookings.handleDotLookup}
      />
      <BookingDeleteDrawer
        key={bookings.bookingToDelete?.bookingId ?? "closed"}
        open={bookings.bookingToDelete != null}
        onClose={bookings.handleCloseDeleteDrawer}
        booking={bookings.bookingToDelete}
        carrierAssignments={bookings.deleteCarrierAssignments}
        containerQuantity={bookings.deleteContainerQuantity}
        isMultipiece={bookings.deleteIsMultipiece}
        canNotifyCarrier={bookings.deleteCanNotifyCarrier}
        initialRecipients={bookings.deleteInitialRecipients}
        initialMessageBody={bookings.deleteInitialMessageBody}
        loading={bookings.deletePreviewLoading}
        submitError={bookings.deleteSubmitError}
        onClearSubmitError={bookings.handleClearDeleteSubmitError}
        onConfirm={bookings.handleConfirmDeleteBooking}
      />
      <BookingDetailDrawer
        open={bookings.bookingDetailOpen}
        loading={bookings.bookingDetailLoading}
        error={bookings.bookingDetailError}
        detail={bookings.bookingDetailModel}
        hideCustomerName
        carrierDetailLink={
          bookings.bookingDetailModel?.carrierId != null
            ? { to: `/carriers/${bookings.bookingDetailModel.carrierId}` }
            : null
        }
        onClose={bookings.handleCloseDetail}
        onDone={bookings.handleCloseDetail}
        onEdit={bookings.bookingDetailModel?.actionsAllowed ? bookings.handleDetailEdit : undefined}
        {...resolveBookingDetailCarrierHandlers({
          detail: bookings.bookingDetailModel,
          singlePieceAssignEnabled: Boolean(bookings.detailAssignToolbarAction?.enabled),
          onAssignOrReassign: bookings.handleDetailReassign,
          onUnassign: bookings.handleDetailUnassignCarrier,
        })}
        assignCarrierMenuLabel={bookings.assignCarrierMenuLabel}
        onDelete={bookings.bookingDetailModel?.actionsAllowed ? bookings.handleDetailDelete : undefined}
      />
    </>
  );
};
